import { API_URL } from "../../config";
import { trackedFetch } from "../../Components/Loader/GlobalLoader";
import { emitLeadUpdated } from "../LeadSystem/leadBus";

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

// ── Types ─────────────────────────────────────────────────────────────

export interface Task {
  id: number;
  lead_id: number | null;
  title: string;
  notes: string | null;
  assigned_to: number | null;
  created_by: number | null;
  due_at: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "pending" | "in_progress" | "completed" | "cancelled";
  completed_at: string | null;
  // Follow-up thread bookkeeping (operational CRM extension).
  last_activity_at?: string | null;
  completed_by?: number | null;
  follow_up_attempts?: number;
  created_at: string;
  lead?: { id: number; lead_number?: string | null; name: string; email: string; phone: string; status: string } | null;
  assignee?: { id: number; username: string; first_name?: string | null; last_name?: string | null } | null;
  creator?: { id: number; username: string } | null;
  completer?: { id: number; username: string; first_name?: string | null; last_name?: string | null } | null;
  // Operational history — populated by getThread() eager-load or fetchTaskAttempts().
  attempts?: LeadContact[];
  // Most-recent attempt (eager-loaded). Drives the task-card preview.
  last_attempt?: {
    id: number;
    type: "call" | "email" | "meeting" | "note";
    outcome: string | null;
    follow_up_outcome: string | null;
    contacted_at: string;
  } | null;
}

export interface UncontactedLead {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  event_type: string | null;
  guest_count: number | null;
  ai_score: string | null;
  created_at: string;
  source: string | null;
  priority: string | null;
  assigned_to?: number | null;
  assignee?: { id: number; username: string; first_name?: string | null; last_name?: string | null } | null;
}

/** Scope for /tasks/my. Server silently demotes 'team' for non-privileged users. */
export type TaskScope = "my" | "team";

export interface MyTasksResponse {
  /** Effective scope server applied (may differ from requested if not privileged). */
  scope: TaskScope;
  /** True when current user may request scope=team. Drives toggle visibility. */
  can_view_team: boolean;
  overdue: Task[];
  today: Task[];
  upcoming: Task[];
  new_leads: UncontactedLead[];
  stats: {
    total: number;
    overdue: number;
    today: number;
    upcoming: number;
    new_leads: number;
    follow_ups_today: number;
  };
}

export interface LeadContact {
  id: number;
  lead_id: number;
  // Parent follow-up task when this contact is an attempt-on-thread.
  task_id?: number | null;
  type: "call" | "email" | "meeting" | "note";
  outcome: string | null;
  /**
   * Richer outcome label used by the follow-up thread modal. May contain
   * values the legacy `outcome` doesn't (email_sent, meeting_scheduled).
   * Renderers should prefer this when present, falling back to `outcome`.
   */
  follow_up_outcome?: string | null;
  notes: string | null;
  contacted_at: string;
  created_by: number | null;
  creator?: { id: number; username: string; first_name?: string | null; last_name?: string | null } | null;
}

// ── Tasks ─────────────────────────────────────────────────────────────

export const fetchMyTasks = async (scope: TaskScope = "my"): Promise<MyTasksResponse> => {
  const qs = scope === "team" ? "?scope=team" : "";
  const res = await trackedFetch(`${API_URL}/tasks/my${qs}`, { headers: headers() });
  const json = await res.json();
  return json.data;
};

export const fetchTasks = async (params?: Record<string, string>): Promise<Task[]> => {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await trackedFetch(`${API_URL}/tasks${qs}`, { headers: headers() });
  const json = await res.json();
  return json.data ?? [];
};

export const fetchLeadTasks = async (leadId: number): Promise<Task[]> => {
  const res = await trackedFetch(`${API_URL}/tasks?lead_id=${leadId}`, { headers: headers() });
  const json = await res.json();
  return json.data ?? [];
};

export const createTask = async (data: {
  title: string; lead_id?: number; assigned_to?: number;
  due_at: string; priority?: string; notes?: string;
}): Promise<Task> => {
  const res = await trackedFetch(`${API_URL}/tasks`, {
    method: "POST", headers: headers(), body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const updateTask = async (id: number, data: Partial<Task>): Promise<Task> => {
  const res = await trackedFetch(`${API_URL}/tasks/${id}`, {
    method: "PUT", headers: headers(), body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const deleteTask = async (id: number): Promise<void> => {
  const res = await trackedFetch(`${API_URL}/tasks/${id}`, {
    method: "DELETE", headers: headers(),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
};

// ── Contacts ──────────────────────────────────────────────────────────

/**
 * Operational history for a single follow-up task — the chronological
 * list of attempts (lead_contacts rows linked via task_id). Powers the
 * TaskDetailDialog's "Attempts" panel when the parent page hasn't
 * eager-loaded them.
 */
export const fetchTaskAttempts = async (taskId: number): Promise<LeadContact[]> => {
  const res = await trackedFetch(`${API_URL}/tasks/${taskId}/attempts`, { headers: headers() });
  const json = await res.json();
  if (!res.ok || !json.status) throw new Error(json.message ?? "Failed to load attempts");
  return json.data ?? [];
};

export const fetchLeadContacts = async (leadId: number): Promise<LeadContact[]> => {
  const res = await trackedFetch(`${API_URL}/leads/${leadId}/contacts`, { headers: headers() });
  const json = await res.json();
  return json.data ?? [];
};

export const logContact = async (data: {
  lead_id: number;
  // Optional: when present, this contact is an ATTEMPT on an existing
  // follow-up task. Server links the row, increments attempts counter,
  // promotes pending→in_progress, and threads the activity meta.
  task_id?: number;
  type: string;
  outcome?: string;
  follow_up_outcome?: string;
  notes?: string;
  contacted_at?: string;
  follow_up?: boolean;
  follow_up_date?: string;
  follow_up_notes?: string;
  // Optional lead-management bundling — populated by the unified
  // "Lead Actions" panel when the rep changes status / priority /
  // assignee / VIP alongside logging the touchpoint. Server applies
  // every change inside the same DB transaction so all activity rows
  // share a request_id and group into ONE timeline card.
  lead_status?: string;
  lead_priority?: string;
  lead_assigned_to?: number | null;
  lead_is_vip?: boolean;
  // Communication template tracking (Phase 2). When the rep applies
  // a template via the Quick Actions toolbar, the id flows through
  // here so analytics can later attribute usage. Visibility lets the
  // surface declare staff-only vs customer-facing on this row.
  template_id?: number | null;
  visibility?: "customer_facing" | "internal" | "private_staff";
}): Promise<{ contact: LeadContact; task: Task | null }> => {
  const res = await trackedFetch(`${API_URL}/lead-contacts`, {
    method: "POST", headers: headers(), body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!json.status) throw new Error(json.message);
  // Broadcast so dashboard widgets (Latest Lead Updates, urgent list,
  // KPIs) refetch and surface the new touchpoint without a manual
  // reload. Mirrors what addLeadNote / updateLead already do.
  emitLeadUpdated(data.lead_id, undefined, { action: "contact_logged", type: data.type });
  return json.data;
};

export const fetchUncontactedLeads = async (): Promise<{ count: number; leads: UncontactedLead[] }> => {
  const res = await trackedFetch(`${API_URL}/leads/view/uncontacted-count`, { headers: headers() });
  const json = await res.json();
  return json.data ?? { count: 0, leads: [] };
};

export const markLeadContacted = async (leadId: number): Promise<void> => {
  await trackedFetch(`${API_URL}/leads/${leadId}/status`, {
    method: "PATCH",
    headers: headers(),
    body: JSON.stringify({ status: "contacted" }),
  });
};

// ── Config ────────────────────────────────────────────────────────────

export const PRIORITY_CONFIG: Record<string, { color: string; label: string }> = {
  low: { color: "#94a3b8", label: "Low" },
  medium: { color: "#3b82f6", label: "Medium" },
  high: { color: "#f59e0b", label: "High" },
  urgent: { color: "#ef4444", label: "Urgent" },
};

export const CONTACT_TYPES: Record<string, { icon: string; label: string; color: string }> = {
  call: { icon: "📞", label: "Call", color: "#3b82f6" },
  email: { icon: "📧", label: "Email", color: "#8b5cf6" },
  meeting: { icon: "🤝", label: "Meeting", color: "#10b981" },
  note: { icon: "📝", label: "Note", color: "#f59e0b" },
};

export const OUTCOME_OPTIONS = [
  { value: "contacted", label: "Contacted", color: "#10b981" },
  { value: "no_answer", label: "No Answer", color: "#f59e0b" },
  { value: "interested", label: "Interested", color: "#3b82f6" },
  { value: "not_interested", label: "Not Interested", color: "#ef4444" },
  { value: "voicemail", label: "Voicemail", color: "#8b5cf6" },
  { value: "callback_requested", label: "Callback Requested", color: "#f97316" },
];

/**
 * Outcome list used inside the follow-up thread modal — superset of
 * OUTCOME_OPTIONS that adds operational labels for email and meeting
 * milestones. Saved to lead_contacts.follow_up_outcome on the server.
 */
export const FOLLOW_UP_OUTCOME_OPTIONS = [
  { value: "contacted",          label: "Contacted",          color: "#10b981" },
  { value: "no_answer",          label: "No Answer",          color: "#f59e0b" },
  { value: "voicemail",          label: "Voicemail",          color: "#8b5cf6" },
  { value: "interested",         label: "Interested",         color: "#3b82f6" },
  { value: "not_interested",     label: "Not Interested",     color: "#ef4444" },
  { value: "callback_requested", label: "Callback Requested", color: "#f97316" },
  { value: "email_sent",         label: "Email Sent",         color: "#0ea5e9" },
  { value: "meeting_scheduled",  label: "Meeting Scheduled",  color: "#22c55e" },
];
